import { motion } from "framer-motion";
import PageHeader from "@/components/site/PageHeader";
import { useSEO } from "@/lib/seo";
import { FIRM } from "@/lib/content";
import { Shield, Mail, Phone } from "lucide-react";

const SECTIONS = [
    {
        title: "Personuppgiftsansvarig",
        body: `Personuppgiftsansvarig för behandlingen är ${FIRM.name} (enskild firma, org.nr ${FIRM.orgNr}).

Kontaktperson i GDPR-frågor är ${FIRM.dpo.name}, ${FIRM.dpo.role}. Du når honom på ${FIRM.dpo.email} eller ${FIRM.dpo.phone}.`,
    },
    {
        title: "Vilka personuppgifter vi behandlar",
        body: `Vi behandlar de uppgifter du själv lämnar till oss (t.ex. namn, e-post, telefon, ärendebeskrivning, personnummer när det krävs), uppgifter som uppstår under uppdraget (korrespondens, handlingar, fakturor) samt tekniska uppgifter (IP-adress, cookies) när du besöker vår webbplats.`,
    },
    {
        title: "Ändamål och laglig grund",
        body: `Vi behandlar uppgifterna för att:

• fullgöra uppdragsavtalet med dig som klient (avtal, art. 6.1 b GDPR)
• uppfylla rättsliga skyldigheter såsom bokföring och penningtvättskontroller (art. 6.1 c)
• tillgodose våra berättigade intressen av att administrera, utveckla och marknadsföra verksamheten samt förebygga intressekonflikter (art. 6.1 f)
• med ditt samtycke skicka nyhetsbrev eller liknande kommunikation (art. 6.1 a)`,
    },
    {
        title: "Hur länge vi sparar uppgifter",
        body: `Klientakter och tillhörande handlingar sparas i minst 10 år efter att uppdraget avslutats i enlighet med preskriptions- och bokföringsregler. Kontaktförfrågningar som inte leder till uppdrag raderas senast efter 12 månader. Nyhetsbrevsdata raderas när du avregistrerar dig.`,
    },
    {
        title: "Mottagare av uppgifter",
        body: `Dina uppgifter kan komma att delas med motparter, domstolar och myndigheter när det krävs för uppdraget, samt med betrodda samarbetspartners (t.ex. IT- och betalningsleverantörer) som behandlar uppgifterna för vår räkning enligt personuppgiftsbiträdesavtal. Uppgifter överförs i regel inte utanför EU/EES.`,
    },
    {
        title: "Dina rättigheter",
        body: `Du har rätt att begära tillgång till, rättelse eller radering av dina personuppgifter, samt att invända mot eller begränsa behandlingen. Du har också rätt till dataportabilitet och att när som helst återkalla samtycken du lämnat. Kontakta ${FIRM.dpo.name} på ${FIRM.dpo.email}.

Anser du att vi behandlar dina uppgifter i strid med GDPR har du rätt att lämna klagomål till Integritetsskyddsmyndigheten (imy.se).`,
    },
    {
        title: "Kakor (cookies)",
        body: `Vi använder nödvändiga kakor för att sajten ska fungera samt valfria analyskakor när du godkänt det via vår kak-banner. Du kan när som helst ändra ditt val genom att rensa webbläsarens lagring för denna webbplats.`,
    },
    {
        title: "Ändringar i policyn",
        body: `Vi uppdaterar denna policy löpande. Den senaste versionen finns alltid här på bmjuridik.se. Väsentliga förändringar meddelas aktiva klienter via e-post.`,
    },
];

export default function Integritetspolicy() {
    useSEO({
        title: "Integritetspolicy",
        description:
            "Så behandlar BM Juridik dina personuppgifter enligt GDPR. Personuppgiftsansvarig är Daniel Madsen.",
    });

    return (
        <div data-testid="page-integritetspolicy">
            <PageHeader
                kicker="Integritetspolicy"
                title="Så hanterar vi"
                accent="dina uppgifter."
                description="Din integritet är vårt ansvar. Denna policy förklarar vilka personuppgifter vi samlar in, varför vi gör det och vilka rättigheter du har enligt dataskyddsförordningen (GDPR)."
            />
            <section className="py-20 bg-[#0A0A0B]">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="bg-[#111113] border border-[#C4A977]/20 p-8 rounded-sm flex items-start gap-4">
                        <span className="w-10 h-10 border border-[#C4A977]/40 grid place-items-center text-[#C4A977] shrink-0">
                            <Shield size={16} />
                        </span>
                        <div>
                            <p className="overline">
                                Personuppgiftsansvarig
                            </p>
                            <p className="mt-2 font-serif text-xl text-[#F4F4F5]">
                                {FIRM.dpo.name}
                            </p>
                            <div className="mt-3 flex flex-wrap gap-4 text-sm text-[#A1A1AA]">
                                <a
                                    href={`mailto:${FIRM.dpo.email}`}
                                    className="inline-flex items-center gap-2 hover:text-[#C4A977] transition-colors"
                                    data-testid="dpo-email"
                                >
                                    <Mail size={12} /> {FIRM.dpo.email}
                                </a>
                                <a
                                    href={`tel:${FIRM.dpo.phoneTel}`}
                                    className="inline-flex items-center gap-2 hover:text-[#C4A977] transition-colors"
                                    data-testid="dpo-phone"
                                >
                                    <Phone size={12} /> {FIRM.dpo.phone}
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12 space-y-10">
                        {SECTIONS.map((s, i) => (
                            <motion.section
                                key={s.title}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-60px" }}
                                transition={{ duration: 0.5, delay: i * 0.04 }}
                                data-testid={`gdpr-section-${i}`}
                            >
                                <p className="overline">
                                    {String(i + 1).padStart(2, "0")}
                                </p>
                                <h2 className="mt-2 font-serif text-3xl text-[#F4F4F5] tracking-tight">
                                    {s.title}
                                </h2>
                                <div className="mt-4 text-[#A1A1AA] leading-relaxed text-[15px] whitespace-pre-wrap">
                                    {s.body}
                                </div>
                            </motion.section>
                        ))}
                    </div>

                    <p className="mt-16 text-xs text-[#A1A1AA]/80">
                        Senast uppdaterad: {new Date().toLocaleDateString("sv-SE", { year: "numeric", month: "long" })}
                    </p>
                </div>
            </section>
        </div>
    );
}
